import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard,
  Newspaper,
  Image,
  GraduationCap,
  UtensilsCrossed,
  FileText,
  Calendar,
  Mail,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Home,
  ChevronLeft,
  User,
  UserCheck,
  Wrench,
  HardDrive,
  Compass,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { ROLE_LABELS, canAccessNav } from '../../lib/utils';

const sidebarItems = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, navKey: 'dashboard' },
  { name: 'Aktuality', href: '/admin/aktuality', icon: Newspaper, navKey: 'news' },
  { name: 'Galerie', href: '/admin/galerie', icon: Image, navKey: 'gallery' },
  { name: 'Třídy', href: '/admin/tridy', icon: GraduationCap, navKey: 'classes' },
  { name: 'Zaměstnanci', href: '/admin/zamestnanci', icon: UserCheck, navKey: 'staff' },
  { name: 'Jídelníček', href: '/admin/jidelnicek', icon: UtensilsCrossed, navKey: 'menu' },
  { name: 'Dokumenty', href: '/admin/dokumenty', icon: FileText, navKey: 'documents' },
  { name: 'Události', href: '/admin/udalosti', icon: Calendar, navKey: 'events' },
  { name: 'Zprávy', href: '/admin/zpravy', icon: Mail, navKey: 'messages' },
  { name: 'Uživatelé', href: '/admin/uzivatele', icon: Users, adminOnly: true, navKey: 'users' },
  { name: 'Stránky', href: '/admin/stranky', icon: Settings, navKey: 'pages' },
  { name: 'Rozcestník', href: '/admin/rozcestnik', icon: Compass, navKey: 'landing', adminOnly: true },
  { name: 'Sezónní motiv', href: '/admin/motiv', icon: Sparkles, navKey: 'theme', adminOnly: true },
  { name: 'Statistika', href: '/admin/statistika', icon: TrendingUp, navKey: 'analytics', adminOnly: true },
  { name: 'Údržba webu', href: '/admin/udrzba', icon: Wrench, adminOnly: true, navKey: 'maintenance' },
  { name: 'Zálohy', href: '/admin/zalohy', icon: HardDrive, adminOnly: true, navKey: 'backup' }
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, canManageUsers } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const filteredItems = sidebarItems.filter(item => {
    if (item.adminOnly && !canManageUsers()) return false;
    // Dashboard je vždy přístupný
    if (item.navKey === 'dashboard') return true;
    if (!canAccessNav(user?.role, item.navKey)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200">
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ECDC4] to-[#95E1D3] flex items-center justify-center text-white font-bold font-accent">
              MŠ
            </div>
            <div>
              <div className="font-semibold text-[#2C3E50]">Administrace</div>
              <div className="text-xs text-[#64748B]">MŠ Nová Role</div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`sidebar-item ${isActive(item.href) ? 'active' : ''}`}
                data-testid={`admin-nav-${item.href.split('/').pop() || 'dashboard'}`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Back to Website */}
        <div className="p-4 border-t border-slate-100">
          <Link to="/" className="sidebar-item text-sm">
            <Home size={18} />
            <span>Zpět na web</span>
          </Link>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ECDC4] to-[#95E1D3] flex items-center justify-center text-white font-bold">
                  MŠ
                </div>
                <span className="font-semibold">Administrace</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {filteredItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`sidebar-item ${isActive(item.href) ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-slate-100">
              <Link to="/" className="sidebar-item text-sm" onClick={() => setSidebarOpen(false)}>
                <Home size={18} />
                <span>Zpět na web</span>
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-mobile-menu"
            >
              <Menu size={24} />
            </button>
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-[#64748B] hover:text-[#4ECDC4]">
              <ChevronLeft size={16} />
              <span>Zpět na web</span>
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-[#E0F7FA] flex items-center justify-center">
                  <User size={16} className="text-[#4ECDC4]" />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-[#64748B]">{ROLE_LABELS[user?.role] || user?.role}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>{user?.name}</div>
                <div className="text-xs font-normal text-[#64748B]">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin/profil" className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profil</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Odhlásit se</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
